/**
 * Who a feature flag reaches in an org, as the Navigation editor describes
 * it (#15403). CLIENT-SAFE: no Prisma, no session — the DB read is
 * `getOrgFeatureAudience` in `@/lib/feature-flags`; this is the pure half.
 *
 * Three states, matching `hasFeatureAccess`'s precedence (retirement pin,
 * user override, role override, org and ancestor overrides, global):
 *
 *   - `org` — on for the org as a whole. `hiddenForPeople` / `hiddenForRoles`
 *     name the user and role overrides that turn it OFF for some people.
 *   - `partial` — off for the org, but user or role overrides turn it on for
 *     some people. `people` and `roles` count those overrides.
 *   - `off` — off for everyone: org level off and nothing enables it, or a
 *     retirement pin to off.
 *
 * Counts, never identities: the editor says how many, not who.
 */

import type { OrgRole } from '@prisma/client-cadence'

import { hasAppAccess, ORG_ROLE_VALUES } from '@/lib/role-utils'

export type FeatureAudience =
  | {
      state: 'org'
      /** People an enabled-for-the-org flag is turned off for. */
      hiddenForPeople?: number
      /** Roles an enabled-for-the-org flag is turned off for. */
      hiddenForRoles?: OrgRole[]
    }
  | { state: 'partial'; people: number; roles: OrgRole[] }
  | { state: 'off' }

/** On for the org with no exclusions: a non-flag gate that passes. */
export const AUDIENCE_ORG: FeatureAudience = Object.freeze({ state: 'org' })

/** Off for everyone: a non-flag gate that fails. */
export const AUDIENCE_OFF: FeatureAudience = Object.freeze({ state: 'off' })

export interface FeatureAudienceInputs {
  /** The org-level answer: pin, org and ancestor overrides, global. */
  orgEnabled: boolean
  /** Whether `orgEnabled` is a retirement pin, which beats every override. */
  pinned: boolean
  /**
   * This org's user overrides for the flag, already narrowed to people with
   * an active membership in the org.
   */
  userOverrides: ReadonlyArray<{ enabled: boolean }>
  /** This org's role overrides for the flag. */
  roleOverrides: ReadonlyArray<{ role: string; enabled: boolean }>
}

/**
 * The roles among `overrides` set to `enabled`, in privilege order. A role
 * no signed-in person holds — `TRACKED`, or a retired string such as
 * `CONTRIBUTOR` that `getPersonRole` never returns — reaches no header, so
 * it is not named.
 */
function rolesSetTo(
  overrides: FeatureAudienceInputs['roleOverrides'],
  enabled: boolean,
): OrgRole[] {
  const set = new Set(
    overrides.filter(o => o.enabled === enabled).map(o => o.role),
  )
  return ORG_ROLE_VALUES.filter(role => set.has(role) && hasAppAccess(role))
}

export function resolveFeatureAudience(
  input: FeatureAudienceInputs,
): FeatureAudience {
  const { orgEnabled, pinned, userOverrides, roleOverrides } = input
  if (pinned) {
    return orgEnabled
      ? { state: 'org', hiddenForPeople: 0, hiddenForRoles: [] }
      : { state: 'off' }
  }
  if (orgEnabled) {
    return {
      state: 'org',
      hiddenForPeople: userOverrides.filter(o => !o.enabled).length,
      hiddenForRoles: rolesSetTo(roleOverrides, false),
    }
  }
  const people = userOverrides.filter(o => o.enabled).length
  const roles = rolesSetTo(roleOverrides, true)
  if (people === 0 && roles.length === 0) return { state: 'off' }
  return { state: 'partial', people, roles }
}
